<div class="collection-flow-container">
  <!-- Authentication Loading Overlay -->
  @if (authenticating()) {
    <lib-authenticating-overlay
      [entityName]="selectedEntityName"
      [integrationEnabled]="isIntegrationEnabled"
      [integrationEntityName]="integrationEntityName"
      [entityIconUrl]="selectedEntityIconUrl"
      [status]="authenticatingStatus()"></lib-authenticating-overlay>
  }

  @if (authenticating() && longWaitDialogState() === 'visible') {
    <lib-long-wait-dialog
      (acknowledge)="onLongWaitDialogAcknowledged()"></lib-long-wait-dialog>
  }

  <!-- Toast Notifications -->
  @if (toastVisible()) {
    <mzic-toast
      [message]="toastMessage()"
      [state]="toastState()"
      position="top"
      (dismiss)="toastVisible.set(false)"></mzic-toast>
  }

  @if (collectionBootstrapError(); as bootstrapError) {
    <div class="error-container collection-bootstrap-error" role="alert">
      <h1>{{ 'lang.collection_load_error_title' | transloco }}</h1>
      @switch (bootstrapError.kind) {
        @case ('NOT_FOUND') {
          <p>{{ 'lang.collection_load_error_not_found' | transloco }}</p>
        }
        @case ('CONTRACT') {
          <p>{{ 'lang.collection_load_error_contract' | transloco }}</p>
        }
        @default {
          <p>{{ 'lang.collection_load_error_transient' | transloco }}</p>
        }
      }
      <div class="error-actions">
        <button
          type="button"
          class="primary-button"
          data-testid="collection-bootstrap-retry"
          (click)="onCollectionBootstrapRetry()">
          {{ 'lang.retry' | transloco | uppercase }}
        </button>
      </div>
    </div>
  } @else if (targetEntryTransition()) {
    @if (targetEntryTransition(); as targetEntry) {
      <lib-target-route-loading-step
        [mean]="targetEntry.mean"
        [errorMessage]="targetRouteLoadError()?.message ?? null"
        (retry)="onTargetRouteRetry()"
        (back)="onTargetRouteErrorBack()" />
    }
  } @else if (transitioningToTarget()) {
    <lib-loading-skeleton></lib-loading-skeleton>
  } @else if (targetRouteLoadError()) {
    <div class="error-container" role="alert">
      <h1>Unable to load this step</h1>
      <p>{{ targetRouteLoadError()?.message }}</p>
      <div class="error-actions">
        <button class="primary-button" (click)="onTargetRouteRetry()">
          RETRY
        </button>
        <button class="secondary-button" (click)="onTargetRouteErrorBack()">
          BACK
        </button>
      </div>
    </div>
  } @else if (connectionPollError()?.kind === 'TRANSIENT') {
    <div class="error-container" role="alert">
      <h1>Unable to confirm this connection</h1>
      <p>{{ connectionPollError()?.message }}</p>
      <div class="error-actions">
        <button class="primary-button" (click)="onConnectionPollRetry()">
          RETRY
        </button>
      </div>
    </div>
  } @else {
    @switch (currentStep()) {
      <!-- Loading State -->
      @case ('loading') {
        <lib-loading-skeleton></lib-loading-skeleton>
      }

      <!-- Forced Entity Resolution Error -->
      @case ('forced-entity-error') {
        <div class="error-container">
          <h1>{{ 'lang.forced_entity_error_title' | transloco }}</h1>
          <p>
            {{
              forcedResolverErrorMessage() ||
                ('lang.forced_entity_error_description' | transloco)
            }}
          </p>
          <div class="error-actions">
            <button
              class="primary-button"
              [disabled]="forcedResolverState() === 'loading'"
              (click)="onForcedEntityRetry()">
              @if (forcedResolverState() === 'loading') {
                <mzic-spinner />
              }
              {{ 'lang.forced_entity_error_retry' | transloco | uppercase }}
            </button>
            <button
              class="secondary-button"
              [disabled]="forcedResolverState() === 'loading'"
              (click)="onForcedEntityBack()">
              {{ 'lang.forced_entity_error_back' | transloco | uppercase }}
            </button>
          </div>
        </div>
      }

      <!-- Passcode Step -->
      @case ('passcode') {
        <lib-passcode-step
          (validated)="onPasscodeValidated($event)"
          [error]="passcodeError()"
          [backoffSeconds]="passcodeBackoffSeconds()"
          [hideBackButton]="true"></lib-passcode-step>
      }

      <!-- Intro Step -->
      @case ('intro') {
        <lib-intro-step
          [alias]="collectionAlias"
          [partnerName]="partnerName"
          [partnerIconUrl]="partnerIconUrl"
          [integrationEnabled]="isIntegrationEnabled"
          [integrationEntityName]="integrationEntityName"
          [loading]="forcedResolverState() === 'loading'"
          (continue)="onIntroContinue()"></lib-intro-step>
      }

      <!-- Entity Selection Step -->
      @case ('entity-selection') {
        @if (currentTargetInfo) {
          <lib-entity-selection-step
            [collectionId]="collectionId"
            [revenueType]="currentTargetInfo.revenueType"
            [revenueSubtype]="currentTargetInfo.revenueSubtype"
            [userName]="collectionAlias"
            (entitySelected)="onEntitySelected($event)"
            (back)="onEntitySelectionBack()"></lib-entity-selection-step>
        }
      }

      <!-- Credentials Step -->
      @case ('credentials') {
        @if (selectedEntity()) {
          <lib-credentials-step
            [collectionId]="collectionId"
            [targetId]="currentTargetInfo?.targetId ?? ''"
            [connectionEntity]="selectedEntity()!"
            [integrationEnabled]="isIntegrationEnabled"
            [integrationEntityName]="integrationEntityName"
            [mode]="credentialsMode()"
            [failureMessage]="currentFailurePresentation?.message ?? null"
            [failureCode]="currentFailurePresentation?.code ?? null"
            [failureOccurredAt]="currentFailurePresentation?.occurredAt ?? null"
            (credentialsSubmitted)="onCredentialsSubmit($event)"
            (back)="onCredentialsBack()"></lib-credentials-step>
        }
      }

      <!-- Auth Interaction Step -->
      @case ('auth-interaction') {
        @if (pendingAuthInteractionState()) {
          <lib-auth-interaction-step
            [authInteraction]="pendingAuthInteractionState()!.authInteraction"
            [entityName]="selectedEntityName"
            [entityIconUrl]="selectedEntityIconUrl"
            [integrationEnabled]="isIntegrationEnabled"
            [integrationEntityName]="integrationEntityName"
            [bannerVariant]="authInteractionBannerVariant()"
            [loading]="authInteractionLoading()"
            [lazyLoading]="authInteractionPageLoading()"
            [resending]="authInteractionResending()"
            [submitError]="authInteractionError()"
            [initialDraftState]="pendingAuthInteractionState()!.draftState"
            (submitRequested)="onAuthInteractionSubmit($event)"
            (resendRequested)="onAuthInteractionResend()"
            (pageRequested)="onAuthInteractionPageRequested($event)"
            (draftChanged)="onAuthInteractionDraftChanged($event)"
            (back)="onAuthInteractionBack()"></lib-auth-interaction-step>
        }
      }

      <!-- Ended verification: the two-factor screen without a pending prompt -->
      @case ('auth-interaction-ended') {
        @if (endedAuthInteraction(); as ended) {
          <lib-auth-interaction-step
            [authInteraction]="ended.view"
            [endedReason]="ended.reason"
            [entityName]="selectedEntityName"
            [entityIconUrl]="selectedEntityIconUrl"
            [integrationEnabled]="isIntegrationEnabled"
            [integrationEntityName]="integrationEntityName"
            (back)="onAuthInteractionEndedBack()"></lib-auth-interaction-step>
        }
      }

      <!-- The provider account must first enroll two-factor on its own site -->
      @case ('provider-setup-required') {
        <lib-provider-setup-required-step
          [providerName]="selectedEntityName"
          (retry)="onProviderSetupRetry()"
          (back)="onProviderSetupBack()" />
      }

      <!-- Completed Connection Step -->
      @case ('connection-completed') {
        @if (completedConnectionAcquisition(); as acquisition) {
          <lib-completed-connection-step
            [acquisition]="acquisition"
            [entity]="completedConnectionEntity()"
            [loadingEntity]="completedConnectionEntityLoading()"
            (back)="onCompletedConnectionBack()"
            (continue)="onCompletedConnectionContinue()" />
        }
      }

      <!-- File Upload Step -->
      @case ('file-upload') {
        @if (currentTargetInfo) {
          <lib-file-upload-step
            [revenueType]="currentTargetInfo.revenueType"
            [revenueSubtype]="currentTargetInfo.revenueSubtype"
            [integrationEnabled]="isIntegrationEnabled"
            [integrationEntityName]="integrationEntityName"
            [isOwner]="isOwner()"
            [hasSharedTarget]="hasSharedCurrentTarget"
            [skipButtonHidden]="skipButtonHidden()"
            (navigationRequested)="onRevisionFileNavigationRequested($event)"
            (primaryActionRequested)="onRevisionFilePrimaryActionRequested()"
            (reviewRequiredInputsRequested)="onRevisionFileReviewRequiredInputs()"
            (retryUploadRequested)="onRevisionFileRetryUpload($event)"
            (share)="onShareClick()"
            (skip)="onFileUploadSkip()"></lib-file-upload-step>
        }
      }

      <!-- Pending Shares Step -->
      @case ('pending-shares') {
        <lib-pending-shares-step
          [partnerName]="partnerName"
          [integrationEnabled]="isIntegrationEnabled"
          [integrationEntityName]="integrationEntityName"
          [shares]="targetShares()"
          [targets]="workflow()?.targets ?? []"
          [loading]="pendingSharesLoading()"
          [collectionId]="collectionId"
          (closeRequested)="onPendingSharesClose()"
          (back)="onPendingSharesBack()"
          (revokeRequested)="
            onShareRevokeRequested($event)
          "></lib-pending-shares-step>
      }

      <!-- Result Step -->
      @case ('result') {
        <lib-result-step
          [success]="isSuccess()"
          [message]="resultMessage()"
          [totalFiles]="totalFiles()"
          [errorDetails]="errorDetails()"
          [partnerName]="partnerName"
          [integrationEnabled]="isIntegrationEnabled"
          [integrationEntityName]="integrationEntityName"
          [loading]="completingCollection()"
          (retry)="onResultRetry()"
          (closeRequested)="onResultClose()"></lib-result-step>
      }

      <!-- Error State -->
      @case ('error') {
        <div class="error-container">
          <h1>Error</h1>
          <p>{{ resultMessage() || 'An error occurred. Please try again.' }}</p>
        </div>
      }
    }
  }

  @if (fileEditorState().pendingDecision?.kind === 'CONFIRM_UPDATE') {
    <lib-file-update-confirmation-dialog
      [direction]="fileEditorState().pendingDecision!.direction"
      [canUpdate]="fileEditorState().canCommitDesiredFileSet"
      [busy]="fileEditorState().busy"
      (updateFiles)="onFileUpdateDecisionConfirm()"
      (discardChanges)="onFilePendingDecisionDiscard()"
      (stay)="onFilePendingDecisionStay()" />
  }

  @if (
    fileEditorState().pendingDecision?.kind === 'CONFIRM_DISCARD_SELECTION'
  ) {
    <lib-file-selection-discard-dialog
      [direction]="fileEditorState().pendingDecision!.direction"
      [fileNames]="fileEditorState().dirtySummary.addedFileNames"
      [busy]="fileEditorState().busy"
      (discard)="onFilePendingDecisionDiscard()"
      (keepEditing)="onFilePendingDecisionStay()" />
  }

  @if (fileEditorState().conflict; as conflict) {
    <lib-file-set-conflict-dialog
      [editable]="conflict.editable"
      [busy]="fileEditorState().busy"
      (rebase)="onFileConflictRebase()"
      (reload)="onFileConflictReload()" />
  }

  @if (showFileValidationIssuesDialog() && fileValidationIssues().length > 0) {
    <lib-file-validation-issues-dialog
      [issues]="presentedFileValidationIssues()"
      (reviewReports)="onFileValidationReviewReports()"
      (removeFiles)="onFileValidationRemoveFiles($event)"
      (backRequested)="onFileValidationBack()" />
  }

  @if (
    showFileRequiredInputsDialog() &&
    currentFileRequiredInputsReview();
    as reviewFile
  ) {
    <lib-file-required-inputs-dialog
      [file]="reviewFile"
      [collectionId]="requiredInputsCollectionId"
      [revisionId]="requiredInputsRevisionId"
      [current]="fileRequiredInputsReviewIndex() + 1"
      [total]="fileRequiredInputsReviewQueue().length"
      [busy]="fileRequiredInputsReviewBusy()"
      [errorMessage]="fileRequiredInputsReviewError()"
      (submitRequiredInputs)="onFileRequiredInputsSubmit($event)"
      (closeRequested)="onFileRequiredInputsReviewClose()" />
  }

  <!-- Target Selection Dialog -->
  @if (showTargetSelectionDialog()) {
    <lib-target-selection-dialog
      [targets]="workflow()?.targets ?? []"
      [integrationEnabled]="isIntegrationEnabled"
      [integrationEntityName]="integrationEntityName"
      [loading]="shareSubmitting()"
      (confirm)="onTargetSelectionConfirm($event)"
      (cancel)="onTargetSelectionCancel()"></lib-target-selection-dialog>
  }

  <!-- Share Info Dialog -->
  @if (showShareInfoDialog() && shareResponse()) {
    <lib-share-info-dialog
      [shareUrl]="shareUrl"
      [passcode]="shareResponse()!.passcode"
      [expiresAt]="shareResponse()!.expires_at ?? ''"
      [integrationEnabled]="isIntegrationEnabled"
      [integrationEntityName]="integrationEntityName"
      (closeRequested)="onShareInfoClose()"></lib-share-info-dialog>
  }

  <!-- Skip Confirmation Dialog -->
  @if (showSkipConfirmationDialog()) {
    <lib-skip-confirmation-dialog
      [partnerName]="partnerName"
      [integrationEnabled]="isIntegrationEnabled"
      [integrationEntityName]="integrationEntityName"
      (confirm)="onSkipConfirm()"
      (cancel)="onSkipCancel()"></lib-skip-confirmation-dialog>
  }

  <!-- Resume Connection Dialog -->
  @if (showResumeDialog() && connectionScreen(); as screen) {
    @if (screen.kind === 'RESUME_OR_CANCEL') {
      <lib-resume-connection-dialog
        [entityName]="screen.connection.entity.display_name"
        [entityIconUrl]="screen.connection.entity.icon_url"
        [connectionStatus]="screen.connection.status"
        [authInteractionTitle]="null"
        [authInteractionInstructions]="null"
        [integrationEnabled]="isIntegrationEnabled"
        [integrationEntityName]="integrationEntityName"
        [loading]="resumeDialogLoading()"
        (resumeRequested)="onResumeConnection()"
        (cancelRequested)="
          onCancelConnectionRequest()
        "></lib-resume-connection-dialog>
    }
  }

  <!-- Cancel Connection Confirmation Dialog -->
  @if (showCancelConfirmDialog()) {
    <lib-cancel-connection-dialog
      [loading]="cancelDialogLoading()"
      (confirm)="onCancelConfirmed()"
      (cancel)="onCancelDismissed()"></lib-cancel-connection-dialog>
  }

  <!-- Waiver Type Selection Dialog -->
  @if (showWaiverTypeDialog()) {
    <lib-waiver-type-selection
      [entityType]="entityType()"
      [userName]="collectionAlias"
      [partnerName]="partnerName"
      [integrationEnabled]="isIntegrationEnabled"
      [integrationEntityName]="integrationEntityName"
      [loading]="waiverLoading()"
      (confirmed)="onWaiverTypeConfirmed($event)"
      (cancel)="onWaiverTypeCancel()"></lib-waiver-type-selection>
  }

  <!-- Business Representative Selection Dialog -->
  @if (showBusinessRepDialog()) {
    <lib-business-rep-selection
      [representatives]="businessRepresentatives()"
      [loading]="businessRepsLoading()"
      [submitting]="waiverLoading()"
      [businessName]="businessName"
      (representativeSelected)="onBusinessRepConfirmed($event)"
      (back)="onBusinessRepBack()"
      (cancel)="onBusinessRepCancel()"></lib-business-rep-selection>
  }

  <!-- Waiver Signing Dialog -->
  @if (showWaiverSigningDialog()) {
    <lib-waiver-dialog
      [waivers]="waiverDocuments()"
      [userName]="collectionAlias"
      [integrationEnabled]="isIntegrationEnabled"
      [integrationEntityName]="integrationEntityName"
      [signingState]="waiverSigningState()"
      [loading]="waiverLoading()"
      (submitRequested)="onWaiverSubmit()"
      (back)="onWaiverSigningBack()"
      (cancelRequested)="onWaiverSigningCancel()"></lib-waiver-dialog>
  }
</div>
